"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const request_maker_library_1 = __importDefault(require("@seneca/request-maker-library"));
const InvalidParamError_1 = __importDefault(require("./errors/InvalidParamError"));
const MalformedGraphQLQueryError_1 = __importDefault(require("./errors/MalformedGraphQLQueryError"));
const MissingParamError_1 = __importDefault(require("./errors/MissingParamError"));
const UnauthorizedError_1 = __importDefault(require("./errors/UnauthorizedError"));
const UrlConstructionError_1 = __importDefault(require("./errors/UrlConstructionError"));
exports.DEFAULT_FETCH_ALL_LIMIT = 100;
/**
 * Client
 * @param {any} fetch an object implementing the fetch api (for browsers this can be fetch.bind() for node require('node-fetch'))
 * @param {Object} options options object input data and dependency injection
 * @param {Object} options.fetch change fetch method from whatwg-fetch (optional, default: whatwg-fetch)
 * @param {string} options.protocol the http protocol of the service endpoint
 * @param {string} options.host the host of the service endpoint
 * @param {string} options.path the path of the service endpoint (optional)
 * @param {number} options.retries the number of times the client should retry on network error / service error (optional, default: 3)
 * @param {number} options.delayInSecs the delay between retries (optional, default: 2)
 * @param {Object} options.credentials identifiers and secrets required to hit protected endpoints (optional)
 * @param {string} options.region states the region of the service for signing (optional, default: eu-west-1)
 * @param {RequestMaker} options.requestMaker for overriding request making logic (optional, default: RequestMaker)
 * @param {string} options.correlationId specifies the correlation id to use in the request (optional)
 * @param {boolean} options.traceLogging sets trace logging on/off (optional, default: false)
 * @param {Function} options.getUserRegion optional function to return a user's region
 * @param {Function} options.getUserLanguage optional function to return a user's language
 * @throws {UrlConstructionError} when either options.protocol or options.host is missing
 */
class Client {
    constructor(fetch, options = {}) {
        if (!fetch || typeof fetch !== 'function') {
            throw new MissingParamError_1.default('fetch');
        }
        this.fetch = fetch;
        this.options = options;
        if (!this.options.protocol || !this.options.host) {
            throw new UrlConstructionError_1.default(this.options.protocol, this.options.host, this.options.path);
        }
        this.url = `${this.options.protocol}://${this.options.host}`;
        if (this.options.path) {
            this.url += this.options.path;
        }
        this.retries = this.options.retries;
        this.delayInSecs = this.options.delayInSecs;
        this.credentials = this.options.credentials;
        this.log = options.log || console.log;
        this.logError = options.logError || console.error;
        const requestMakerOpts = {
            retries: this.retries,
            delayInSecs: this.delayInSecs,
            credentials: this.credentials,
            region: this.options.region || 'eu-west-1',
            correlationId: this.options.correlationId,
            traceLogging: this.options.traceLogging || false,
            signal: this.options.signal || undefined,
            getUserRegion: this.options.getUserRegion,
            getUserLanguage: this.options.getUserLanguage,
            log: this.log,
            logError: this.logError
        };
        const getCredentials = this.credentials
            ? () => __awaiter(this, void 0, void 0, function* () { return this.credentials; })
            : undefined;
        this.requestMaker =
            this.options.requestMaker ||
                new request_maker_library_1.default({
                    fetch: this.fetch,
                    options: requestMakerOpts,
                    getCredentials
                });
    }
    get credentials() {
        return this.credentials_;
    }
    set credentials(value) {
        this.credentials_ = value;
        if (this.requestMaker && value) {
            this.requestMaker.getCredentials = () => __awaiter(this, void 0, void 0, function* () { return value; });
        }
    }
    /**
     * Performs and returns a GraphQL query. This method will always lookup the
     * POST /api/graphql on the endpoint
     * @param {string} query
     * @param {Object<string, string|number|Array<string>|Array<number>>} variables
     * @throws {MalformedGraphQLQueryError} thrown when query syntax is malformed
     * @throws {UnauthorizedError} when unauthorized to query the graphql endpoint
     */
    graphQL(query, variables) {
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                query,
                variables
            };
            const response = yield this.requestMaker.makeRequest(`${this.url}/api/graphql`, {
                method: 'POST',
                body: JSON.stringify(body)
            }, [200, 400]);
            const json = response.json;
            if (response.status === 400) {
                const errors = (json && json.errors) || [];
                const graphQLError = errors[0] || {};
                throw new MalformedGraphQLQueryError_1.default(JSON.stringify(graphQLError));
            }
            if (json && json.errors) {
                const error = json.errors[0];
                if (error.extensions &&
                    error.extensions.exception &&
                    error.extensions.exception.name === 'UnauthorizedError') {
                    let reason = 'No credentials are present';
                    if (this.credentials && this.credentials.accessToken) {
                        reason = 'Credentials are invalid';
                    }
                    throw new UnauthorizedError_1.default(reason);
                }
            }
            return response.json;
        });
    }
    _pageMethod({ method, pageToItems = page => page.items, options } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof method !== 'function') {
                throw new InvalidParamError_1.default('method', '', `recieved ${typeof method} must be a function`);
            }
            let items = [];
            let lastKey;
            do {
                const page = yield method(Object.assign(Object.assign({}, options), { lastKey }));
                lastKey = page.lastKey;
                items = items.concat(pageToItems(page));
            } while (lastKey);
            return items;
        });
    }
    _generatePagingQueryParams({ limit = exports.DEFAULT_FETCH_ALL_LIMIT, lastKey } = {}) {
        const params = [];
        limit && params.push(`limit=${limit}`);
        lastKey && params.push(`lastKey=${encodeURIComponent(lastKey)}`);
        return params;
    }
    _validateMissingParams(args) {
        Object.keys(args).forEach(argName => {
            const arg = args[argName];
            if (arg === undefined || arg === null) {
                throw new MissingParamError_1.default(argName);
            }
        });
    }
    version() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.requestMaker.makeRequest(`${this.url}/api/version`, {
                method: 'GET'
            }, [200]);
            return response.json;
        });
    }
}
exports.default = Client;
